import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { TrendingUp, UserX, RefreshCw, CheckCheck } from 'lucide-react'
import styles from './CSMHistory.module.css'
import AuthContext from '../../store/auth-context'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import { smPost, formatActivityTimestamp } from './smApi'
import { emojiForOccasionType } from './smIcons'

const SM_GET_HISTORY_SUMMARY = 'sm_get_history_summary'
const SM_GET_RECENT_ACTIVITY = 'sm_get_recent_activity'
const ACTIVITY_PAGE_SIZE = 20

const CSMHistory = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [summary, setSummary] = useState(null)
    const [loading, setLoading] = useState(true)
    const [loadErrorMessage, setLoadErrorMessage] = useState(null)

    const [activity, setActivity] = useState([])
    const [hasMoreActivity, setHasMoreActivity] = useState(false)
    const [activityLoading, setActivityLoading] = useState(true)
    const [activityErrorMessage, setActivityErrorMessage] = useState(null)
    const [loadingMore, setLoadingMore] = useState(false)

    const loadSummary = useCallback(async () => {
        setLoading(true)
        setLoadErrorMessage(null)
        const res = await smPost(authCtx, activeStore, SM_GET_HISTORY_SUMMARY)
        if (res?.status === 'success' && res?.response) {
            setSummary(res.response)
        } else {
            setLoadErrorMessage(res?.message || "Couldn't load celebration history right now.")
        }
        setLoading(false)
    }, [authCtx, activeStore])

    const loadActivity = useCallback(async (offset, { append } = {}) => {
        if (append) setLoadingMore(true)
        else setActivityLoading(true)
        setActivityErrorMessage(null)

        const res = await smPost(authCtx, activeStore, SM_GET_RECENT_ACTIVITY, {
            limit: ACTIVITY_PAGE_SIZE,
            offset,
        })

        if (res?.status === 'success' && res?.response) {
            const nextActivity = res.response.activity || []
            setActivity((prev) => (append ? [...prev, ...nextActivity] : nextActivity))
            setHasMoreActivity(!!res.response.has_more)
        } else {
            setActivityErrorMessage(res?.message || "Couldn't load recent activity right now.")
        }

        if (append) setLoadingMore(false)
        else setActivityLoading(false)
    }, [authCtx, activeStore])

    useEffect(() => {
        if (activeStore?.storeIdentifier) {
            loadSummary()
            loadActivity(0)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier])

    const loadMoreActivity = () => loadActivity(activity.length, { append: true })

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading celebration history…</span>
            </div>
        )
    }

    if (loadErrorMessage || !summary) {
        return (
            <div className={styles.loadingWrap}>
                <span>{loadErrorMessage || "Couldn't load celebration history right now."}</span>
                <button type="button" className={styles.retryBtn} onClick={loadSummary}>Retry</button>
            </div>
        )
    }

    const { today, yesterday, month, wish_rate: wishRate, never_wished: neverWished } = summary

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <h1 className={styles.pageTitle}>Celebration History</h1>
                <p className={styles.pageSubtitle}>A day-by-day record of every wish sent.</p>
            </div>

            <div className={styles.statGrid}>
                <div className={styles.statTile}>
                    <div className={styles.statLabel}>Today</div>
                    <div className={styles.statValue}>{today} Wishes Sent</div>
                </div>
                <div className={styles.statTile}>
                    <div className={styles.statLabel}>Yesterday</div>
                    <div className={styles.statValue}>{yesterday} Wishes</div>
                </div>
                <div className={styles.statTile}>
                    <div className={styles.statLabel}>This Month</div>
                    <div className={styles.statValue}>{month} Wishes</div>
                </div>
            </div>

            <div className={styles.highlightRow}>
                <div className={styles.highlightCard}>
                    <div className={styles.highlightIconWrap}>
                        <TrendingUp size={17} />
                    </div>
                    <div>
                        <div className={styles.highlightValue}>{wishRate}%</div>
                        <div className={styles.highlightLabel}>Wish Rate</div>
                    </div>
                </div>
                <Link to="/special-moments/inbox" className={styles.highlightCard}>
                    <div className={`${styles.highlightIconWrap} ${styles.highlightIconWarn}`}>
                        <UserX size={17} />
                    </div>
                    <div>
                        <div className={styles.highlightValue}>{neverWished}</div>
                        <div className={styles.highlightLabel}>Never Wished</div>
                    </div>
                </Link>
            </div>

            <div className={styles.sectionLabel}>Recent Activity</div>

            {activityLoading && (
                <div className={styles.activityLoadingWrap}>
                    <RefreshCw size={15} className={styles.spinner} /> Loading recent activity…
                </div>
            )}

            {!activityLoading && activityErrorMessage && (
                <div className={styles.activityLoadingWrap}>
                    {activityErrorMessage}{' '}
                    <button type="button" className={styles.retryBtn} onClick={() => loadActivity(0)}>Retry</button>
                </div>
            )}

            {!activityLoading && !activityErrorMessage && activity.length === 0 && (
                <div className={styles.activityLoadingWrap}>No wishes sent yet.</div>
            )}

            {!activityLoading && !activityErrorMessage && activity.length > 0 && (
                <>
                    <div className={styles.activityList}>
                        {activity.map((a, idx) => (
                            <Link
                                key={`${a.client_store_relation_identifier}-${idx}`}
                                to={`/special-moments/customer/${a.client_store_relation_identifier}`}
                                className={styles.activityRow}
                            >
                                <span className={styles.activityCheck}><CheckCheck size={15} /></span>
                                <span className={styles.activityEmoji}>{emojiForOccasionType(a.occasion_type)}</span>
                                <div className={styles.activityText}>
                                    <span className={styles.activityName}>{a.name}</span>
                                    <span className={styles.activityMeta}>{a.via} • {formatActivityTimestamp(a.at)}</span>
                                </div>
                            </Link>
                        ))}
                    </div>

                    {hasMoreActivity && (
                        <button
                            type="button"
                            className={styles.loadMoreBtn}
                            onClick={loadMoreActivity}
                            disabled={loadingMore}
                        >
                            {loadingMore ? <RefreshCw size={13} className={styles.spinner} /> : null}
                            {' '}{loadingMore ? 'Loading…' : 'Load More'}
                        </button>
                    )}
                </>
            )}
        </div>
    )
}

export default CSMHistory
